/**
 * /pos — CATÁLOGO dinâmico das pós-graduações.
 *
 * Comportamento:
 *  - Sem campanha ativa → exibe o catálogo padrão de pós-graduações.
 *  - Com campanha preset (ex: plantaofeo) → exibe a LP espelho (mirrorRoute).
 *  - Com cupom livre → exibe o catálogo padrão + banner sinalizando a ação.
 *
 * Estado da campanha vem do site_config (chave `checkout.campanha-ativa`),
 * gerenciado em /admin/campanhas.
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { redirecionaCatalogoBlackfeo } from "@/lib/black-november/ofertas-lps";

import { POS_INFO, ALL_PRODUCTS } from "@/lib/checkout/links";
import { PosCatalogoPage } from "@/components/lps/PosCatalogoPage";
import { PlantaofeoPage } from "@/components/lps/PlantaofeoPage";
import { SemanaFisioPage } from "@/components/lps/SemanaFisioPage";
import { getActiveCampaignFn } from "@/lib/checkout/campanhas.functions";

export const Route = createFileRoute("/pos")({
  beforeLoad: ({ location }) => {
    const destino = redirecionaCatalogoBlackfeo(location.searchStr, Date.now());
    if (destino) throw redirect({ href: destino });
  },
  loader: () => getActiveCampaignFn(),
  head: () => {
    const itemList = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Pós-Graduações FEO",
      url: "https://fisioemortopedia.com.br/pos",
      description:
        "Catálogo das 6 pós-graduações Lato Sensu da Fisio em Ortopedia, 100% online e reconhecidas pelo MEC.",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: ALL_PRODUCTS.map((slug, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "Course",
            name: POS_INFO[slug].nome,
            url: POS_INFO[slug].wix,
            provider: {
              "@type": "EducationalOrganization",
              name: "Fisio em Ortopedia",
              url: "https://fisioemortopedia.com.br",
            },
          },
        })),
      },
    };
    return {
      meta: [
        { title: "Pós em Fisioterapia Online Reconhecida pelo MEC | FEO" },
        {
          name: "description",
          content:
            "Pós-graduação online em fisioterapia, reconhecida pelo MEC, em 6 áreas: Ortopedia, Dor, Esporte, Gerontologia, Traumatologia e Prescrição de Exercícios. Mais de 80 professores e 15 mil alunos.",
        },
        { property: "og:title", content: "Pós em Fisioterapia Online Reconhecida pelo MEC | FEO" },
        {
          property: "og:description",
          content: "6 pós online reconhecidas pelo MEC: Ortopedia, Dor, Esporte, Gerontologia, Traumatologia e Prescrição de Exercícios. Escolha a sua área.",
        },
        { property: "og:url", content: "https://fisioemortopedia.com.br/pos" },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/uJh6Ec5dB7QIUilgPzSo9fT36WL2/social-images/social-1783178899843-ChatGPT_Image_4_de_jul._de_2026,_12_28_08.webp" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/uJh6Ec5dB7QIUilgPzSo9fT36WL2/social-images/social-1783178899843-ChatGPT_Image_4_de_jul._de_2026,_12_28_08.webp" },
        { name: "twitter:title", content: "Pós em Fisioterapia Online Reconhecida pelo MEC | FEO" },
        { name: "twitter:description", content: "6 pós online reconhecidas pelo MEC em Ortopedia, Dor, Esporte, Gerontologia, Traumatologia e Prescrição." },
      ],
      links: [{ rel: "canonical", href: "https://fisioemortopedia.com.br/pos" }],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(itemList) }],
    };
  },
  component: CatalogoPosDynamic,
});

function CatalogoPosDynamic() {
  const campaign = Route.useLoaderData();

  // Campanha com página espelho: renderiza a LP mirror da ação
  if (campaign?.mirrorRoute === "/plantaofeo") {
    return <PlantaofeoPage />;
  }
  if (campaign?.mirrorRoute === "/semanadafisio") {
    return <SemanaFisioPage ativa />;
  }

  // Campanha custom (cupom livre): catálogo padrão + banner sinalizando ação
  if (campaign) {
    return <PosCatalogoPage campaignBanner={{ nome: campaign.nome, cupom: campaign.cupom }} />;
  }

  // Sem campanha → catálogo padrão
  return <PosCatalogoPage />;
}
